/**
 * Ícones de marcas (redes / plataformas) via [Simple Icons](https://simpleicons.org/) (CC0-1.0).
 * Em produção, respeite as guidelines oficiais de cada marca ao exibir logos.
 *
 * @see https://github.com/simple-icons/simple-icons/blob/develop/LICENSE.md
 */
import {
  siFacebook,
  siGmail,
  siGoogle,
  siGoogleads,
  siGooglechrome,
  siInstagram,
  siLinktree,
  siMeta,
  siTiktok,
  siWhatsapp,
  siWordpress,
  siYoutube,
} from 'simple-icons';
import { ICO } from './uiIcons.js';

function pick(icon) {
  return { path: icon.path, hex: icon.hex, title: icon.title };
}

// O LinkedIn foi removido do simple-icons (pedido da marca), então o ícone vem
// como valor literal — path oficial (viewBox 24) + azul da marca.
const LINKEDIN_ICON = {
  title: 'LinkedIn',
  hex: '0A66C2',
  path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z',
};

/** Dados mínimos por slug usado em `ACESSO_TIPOS` e em telas React */
export const BRAND_ICONS_BY_SLUG = {
  instagram: pick(siInstagram),
  facebook: pick(siFacebook),
  linkedin: LINKEDIN_ICON,
  meta: pick(siMeta),
  googleads: pick(siGoogleads),
  youtube: pick(siYoutube),
  tiktok: pick(siTiktok),
  gmail: pick(siGmail),
  linktree: pick(siLinktree),
  google: pick(siGoogle),
  googlechrome: pick(siGooglechrome),
  wordpress: pick(siWordpress),
  // (2.12) Entrou porque estava FORA do registro: o glifo do WhatsApp vinha escrito
  // à mão, duas vezes, no CrmPage — o mesmo path copiado nos dois botões «Abrir
  // WhatsApp». Era o quarto renderizador de logo, e o único que nem passava por aqui.
  whatsapp: pick(siWhatsapp),
};

/**
 * (2.12) A RÉGUA DA TINTA — quem decide é o FUNDO, não a marca.
 * ──────────────────────────────────────────────────────────────────────────
 * O app tinha três renderizadores de logo com três respostas para a mesma
 * pergunta: o do modal de Acessos pintava com o hex da marca, o do cartão de
 * Produtividade também, e o do Tráfego Pago pintava com `currentColor`. Não era
 * gosto de cada tela — era pergunta nunca respondida.
 *
 * Medido na bancada (bancada-marcas.html, 12 marcas × 2 temas, razão WCAG):
 *
 *   Sobre o FUNDO DO TEMA, o hex da marca reprova em 7 das 12. Não é margem:
 *   o TikTok é #000000 e mede 1,25:1 sobre o painel escuro — some. Tanto que
 *   já existia um remendo à mão no App.css forçando o preto do TikTok para
 *   #e2e8f0 só no tema escuro, só naquele cartão. O Linktree faz o inverso
 *   (verde claro, 1,67:1 no tema claro), o LinkedIn dá 2,96 e o Meta 3,21.
 *   Nenhuma paleta de marca foi desenhada para os DOIS fundos de um app.
 *
 *   Sobre um FUNDO PRÓPRIO E FIXO, o hex passa: o quadrado pastel do modal de
 *   Acessos leva `style="background:#…"` inline, então o par cor×fundo é o
 *   MESMO nos dois temas — dá para medir uma vez e garantir para sempre.
 *   11 das 12 passam de 3:1 ali (a 12.ª, o Linktree, está consertada abaixo).
 *
 * Daí a régua, que é uma pergunta com resposta verificável — «o glifo tem
 * fundo próprio e fixo?» — e não uma opinião sobre reconhecimento de marca:
 *
 *   MARCA → só onde há fundo próprio e fixo. Hoje é um lugar só: o quadrado
 *           de 38px do modal de Acessos, onde o logo é a âncora visual do
 *           cartão e tem o rótulo escrito ao lado.
 *   TEMA  → em qualquer fundo que muda com o tema (tabela, lista, aba, pílula,
 *           cartão). Ali a marca não tem como garantir nada.
 *
 * Efeito colateral bem-vindo: `currentColor` devolve a vida a estados que já
 * estavam escritos e não funcionavam. `.cl-ig-icon-wrap:hover` mudava a `color`
 * havia meses sobre um glifo de `fill` fixo — hover declarado e inerte.
 *
 * A catraca é `marcasTinta.test.js`: cor de marca fora de fundo fixo, ou par
 * cor×fundo abaixo de 3:1, quebra o teste.
 */
export const MARCA_TINTA = {
  /** Fundo próprio e fixo (o quadrado pastel do modal Acessos) → hex da marca. */
  MARCA: 'marca',
  /** Fundo do tema → `currentColor`. É o padrão: na dúvida, é este. */
  TEMA: 'tema',
};

/**
 * (2.12) A MEDIDA — três, e a de linha é a do 2.10.
 *
 * Eram cinco tamanhos sem critério: 15 no Tráfego, 18 na tabela de Clientes, 20
 * no cartão de Produtividade, 22 no chip de Acessos e 34 no estado vazio.
 * Medido na tela, os três primeiros são o MESMO papel — glifo ao lado de texto —
 * e destoavam da vizinhança: na tabela de Clientes o logo saía a 18px encostado
 * numa chave de 15px, repetido em 18 linhas.
 *
 * LINHA reusa `ICO.BOTAO` em vez de inventar número: pela definição do 2.10, é
 * «ícone ao lado de texto», que é exatamente o caso dos três.
 * CHIP e VAZIO ficam de fora da escala de ícone de propósito — não são ícones de
 * interface, são o azulejo da marca (dentro de um quadrado de 38px) e a
 * ilustração de um estado vazio.
 */
export const MARCA_ICO = {
  LINHA: ICO.BOTAO,
  CHIP: 22,
  VAZIO: 34,
};

/**
 * O ÚNICO lugar que decide geometria, tinta e medida de um glifo de marca.
 * Os dois emissores (JSX em `IcoMarca.jsx`, string aqui embaixo) só desenham o
 * que esta função resolveu — nenhum dos dois escolhe cor.
 *
 * @param {string} slug - instagram, youtube, googleads, …
 * @param {{ tinta?: string, size?: number }} [opts]
 * @returns {{ path: string, title: string, fill: string, size: number } | null}
 *   `null` quando o slug não é uma marca do registro — quem chama decide o que
 *   pôr no lugar (o modal de Acessos põe a chave).
 */
export function glifoDeMarca(slug, opts = {}) {
  const data = BRAND_ICONS_BY_SLUG[String(slug || '').toLowerCase()];
  if (!data) return null;
  const { tinta = MARCA_TINTA.TEMA, size = MARCA_ICO.LINHA } = opts;
  return {
    path: data.path,
    title: data.title,
    size: Number(size) || MARCA_ICO.LINHA,
    fill: tinta === MARCA_TINTA.MARCA ? `#${data.hex}` : 'currentColor',
  };
}

function escAttr(v) {
  return String(v ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Emissor STRING — para o mundo imperativo (`messaAppLogic`, que monta HTML).
 * O emissor JSX é `components/IcoMarca.jsx`; os dois saem do mesmo `glifoDeMarca`.
 *
 * @param {string} slug
 * @param {{ tinta?: string, size?: number, className?: string, titulo?: string }} [opts]
 * @returns {string} `''` se o slug não for uma marca do registro.
 */
export function marcaSvgHtml(slug, opts = {}) {
  const g = glifoDeMarca(slug, opts);
  if (!g) return '';
  const cls = opts.className ? ` class="${escAttr(opts.className)}"` : '';
  const rotulo = opts.titulo === undefined ? g.title : opts.titulo;
  // Com rótulo, o `<title>` do SVG faz as duas coisas de uma vez: anuncia no leitor
  // de tela e mostra a dica ao passar o mouse. Sem rótulo o glifo é enfeite ao lado
  // de um texto que já diz o mesmo, e some da leitura.
  const atrs = rotulo
    ? ` role="img" aria-label="${escAttr(rotulo)}"`
    : ' role="img" aria-hidden="true"';
  const titulo = rotulo ? `<title>${escAttr(rotulo)}</title>` : '';
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${g.size}" height="${g.size}" ` +
    `viewBox="0 0 24 24"${cls}${atrs}>${titulo}` +
    `<path fill="${g.fill}" d="${g.path}"/></svg>`
  );
}
